import Resource, { createResourceHook } from '@tabeo/resync'
import api from '@tabeo/sharpei/utils/api'
import qs from 'qs'
import { useLocation } from 'react-router'
import normalize, { normalizeEntityLinks } from '../normalizers/ticket'
import { ticketsEvents } from '../Tickets'
import subResources from './subResources'

function mergeWithLocal(local, remote) {
  if (local && new Date(local.updatedAt) > new Date(remote.updatedAt)) {
    return {
      ...remote,
      ...local,
    }
  }
  return {
    ...local,
    ...remote,
  }
}

const systemUser = {
  firstName: 'System',
  lastName: '',
  email: 'system@tabeo.co.uk',
}

const getCreator = async ticket => {
  if (ticket.creatorID) {
    const { admin } = await api.get(
      `/admins/${ticket.creatorID}?subResource=user`
    )
    return {
      ...ticket.creator,
      ...admin,
      ...admin.user,
    }
  }
  return systemUser
}

const getResolver = async ticket => {
  if (ticket.resolverID) {
    // TODO: add logic for other user types
    const { admin } = await api.get(
      `/admins/${ticket.resolverID}?subResource=user`
    )
    return {
      ...ticket.resolver,
      ...admin,
      ...admin.user,
    }
  }
  return systemUser
}

class Ticket extends Resource {
  async fetch() {
    if (this.params.id) {
      const { ticket } = await api.get(
        `/tickets/${this.params.id}?${subResources}`
      )

      const [creator, resolver] = await Promise.all([
        getCreator(ticket),
        getResolver(ticket),
      ])

      const ticketWithResources = {
        ...ticket,
        creator,
        resolver,
      }

      this.data = normalize(mergeWithLocal(this.data, ticketWithResources))
    }
  }

  update = async data => {
    const lastUpdatedAt = this.data.updatedAt
    const normalized = normalizeEntityLinks(data)
    const payload = {
      ...normalized,
      countryCodes: [
        ...new Set(
          normalized.entities.map(e => e.data?.countryCode).filter(Boolean) ||
            []
        ),
      ],
    }

    // Optimistic update
    this.data = normalize({
      ...payload,
      updatedAt: new Date(),
      localUpdateError: null,
    })
    try {
      const response = await api.put(`/tickets/${this.params.id}`, payload)
      ticketsEvents.triggerEvent('update', response.ticket)
      return response
    } catch (e) {
      // Set the error and keep the optimistic value, but revert the updatedAt
      this.data = {
        ...this.data,
        localUpdateError: e,
        updatedAt: lastUpdatedAt,
      }
    }
  }

  remove = async () => {
    const response = await api.delete(`/tickets/${this.params.id}`, {})
    ticketsEvents.triggerEvent('remove', this.data)

    return response
  }

  toggleField = async field => {
    const lastData = this.data

    const newState = {
      ...this.data,
      [field]: !this.data[field],
      updatedAt: new Date(),
    }
    this.data = { ...normalize(newState), localUpdateError: null }
    try {
      const response = await api.put(`/tickets/${this.params.id}`, newState)
      ticketsEvents.triggerEvent('update', response.ticket)
      return response
    } catch (e) {
      // Set the error and revert the data to show the correct ticket state
      this.data = { ...lastData, localUpdateError: e }
    }
  }

  uploadFile = async file => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append(
      'instruction',
      JSON.stringify({
        type: 'ticket_file',
        countryCode: 'gb',
      })
    )

    const response = await api.post(
      `/tickets/${this.params.id}/uploads`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      }
    )

    await this.fetch()
    return response
  }

  removeFile = async id => {
    const response = await api.delete(`/uploads/${id}`, {})
    await this.fetch()
    return response
  }

  invalidateCache = () => {
    this.data = null
  }
}

export default Ticket
export const useResource = createResourceHook(Ticket)
export function useTicket(params) {
  const { search } = useLocation()
  const { ticketId } = qs.parse(search, { ignoreQueryPrefix: true })

  const { resource, ...rest } = useResource(() => {
    const { id } =
      typeof params === 'function'
        ? params()
        : params || { id: Number(ticketId) }
    if (!id) {
      throw new Error('Ticket ID is null')
    }

    return {
      id,
    }
  })

  // React.useEffect(() => {
  //   const unsubscribe = resource.on?.('update', updatedTicket => {
  //     ticketsEvents.triggerEvent('update', updatedTicket)
  //   })
  //   return () => {
  //     unsubscribe && unsubscribe()
  //   }
  // }, [resource])

  return { resource, ...rest }
}
